import React, {useEffect, useMemo, useState} from 'react'
import moment from 'moment-timezone'
import type {CallInfo} from '../../../context/PresenceContext'
import {OpiCardSkeleton} from './OpiCardSkeleton'

type Row = {
  id: string
  time: string
  customer: string
  requester: string
  duration: string
  durationSeconds: number
  language: string
}

type Props = {
  title: string
  calls: CallInfo[]
  isLoading?: boolean
  /** queue = hold time from queue entry; live = connected-to-agent duration (matches Ops). */
  durationMode?: 'queue' | 'live'
}

const EASTERN = 'America/New_York'
const WARN_SECONDS = 120

export const OpiCallsTable: React.FC<Props> = ({
  title,
  calls,
  isLoading,
  durationMode = 'queue',
}) => {
  const [now, setNow] = useState(moment.tz(EASTERN))

  useEffect(() => {
    const t = setInterval(() => setNow(moment.tz(EASTERN)), 1000)
    return () => clearInterval(t)
  }, [])

  const rows = useMemo(() => mapCalls(calls, now, durationMode), [calls, now, durationMode])

  if (isLoading && rows.length === 0) {
    return <OpiCardSkeleton variant='table' />
  }

  return (
    <div className='opi-table-card'>
      <div className='opi-table-card__header'>
        <h3>
          {title} - {rows.length} Calls
        </h3>
      </div>
      <div className='opi-table-card__scroll'>
        <table>
          <thead>
            <tr>
              <th>Call ID</th>
              <th>Time</th>
              <th>Customer</th>
              <th>Requester</th>
              <th>Queue Duration</th>
              <th>Language</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className='opi-empty'>
                  No matching records found
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td style={{fontWeight: 500}}>{row.id}</td>
                  <td style={{color: '#565d6d'}}>{row.time}</td>
                  <td>{row.customer}</td>
                  <td>{row.requester}</td>
                  <td
                    className={
                      row.durationSeconds >= WARN_SECONDS ? 'duration--warning' : undefined
                    }
                  >
                    {row.duration}
                  </td>
                  <td>{row.language}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Queue clock uses the earlier of queue entry and call start, so a late presence stamp does not sit at 00:00. */
function earliestTimestamp(...values: Array<string | null | undefined>): string | undefined {
  const parsed = values
    .filter((value): value is string => !!value)
    .map((value) => ({value, at: moment.utc(value)}))
    .filter((item) => item.at.isValid())
  if (parsed.length === 0) return undefined
  return parsed.reduce((earliest, item) => (item.at.isBefore(earliest.at) ? item : earliest)).value
}

function mapCalls(
  calls: CallInfo[],
  now: moment.Moment,
  durationMode: 'queue' | 'live'
): Row[] {
  return calls.map((item) => {
    const durationSource =
      durationMode === 'live'
        ? item.connectedToAgentTimestamp || item.queueTimestamp || item.requestedTime
        : earliestTimestamp(item.queueTimestamp, item.requestedTime)

    let durationSeconds = 0
    let duration = '—'
    if (durationSource) {
      const startMoment = moment.utc(durationSource)
      durationSeconds = Math.max(0, now.diff(startMoment, 'seconds'))
      const m = Math.floor(durationSeconds / 60)
      const s = durationSeconds % 60
      duration = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    }

    const timeSource = item.requestedTime || item.queueTimestamp || item.connectedToAgentTimestamp
    const time = timeSource ? moment.utc(timeSource).tz(EASTERN).format('hh:mm A') : '—'

    return {
      id: item.callId ? `#C-${item.callId}` : '—',
      time,
      customer: item.customer || '—',
      requester: item.requester || '—',
      duration,
      durationSeconds,
      language: item.languageTo || item.languageFrom || '—',
    }
  })
}
